import React from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileText, UserCheck, Calendar, CreditCard, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const AdmissionPage = () => {
  const steps = [
    {
      icon: FileText,
      title: 'Fill Application Form',
      description: 'Complete the online enquiry form with your details and course preference'
    },
    {
      icon: UserCheck,
      title: 'Entrance Test (Optional)',
      description: 'Appear for entrance test or provide previous academic records for direct admission'
    },
    {
      icon: Calendar,
      title: 'Counseling Session',
      description: 'Attend a free counseling session to discuss course details and batch timings'
    },
    {
      icon: CreditCard,
      title: 'Fee Payment',
      description: 'Complete the fee payment process with available EMI and scholarship options'
    },
    {
      icon: BookOpen,
      title: 'Welcome to Mantra Prayas',
      description: 'Receive study material and join your batch to start your success journey'
    }
  ];

  const documents = [
    'Recent passport-size photographs (2)',
    'Previous class mark sheets and certificates',
    'Transfer certificate (if applicable)',
    'Identity proof (Aadhar card)',
    'Address proof'
  ];

  return (
    <div>
      <PageHeader
        title="Admission Process"
        subtitle="Simple and transparent admission procedure"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Admission' }
        ]}
      />

      <section className="section-padding bg-background">
        <div className="container-custom max-w-5xl">
          {/* Process Steps */}
          <div className="space-y-8 mb-16">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-2 hover:border-primary transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className="h-16 w-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <step.icon className="h-6 w-6 text-primary" />
                          <h3 className="text-xl font-heading font-bold text-foreground">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Documents Required */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold text-foreground mb-6">
                  Documents Required
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {documents.map((doc, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{doc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Card className="bg-primary text-primary-foreground">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold mb-4">Ready to Get Started?</h3>
                <p className="text-primary-foreground/90 mb-6">
                  Begin your admission process today and join thousands of successful students
                </p>
                <Link to="/enquiry">
                  <Button size="lg" className="bg-secondary hover:bg-secondary-dark text-secondary-foreground">
                    Start Application
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AdmissionPage;
